/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
import { IModelApp, IModelConnection, ScreenViewport, ViewClipTool, ViewState } from "@itwin/core-frontend";
import { ContextRealityModelProps, ContextRealityModels } from "@itwin/core-common";
import { RealityDataFormat, RealityDataProvider, RealityDataSourceKey } from "@itwin/core-common";
import { ContextRealityModelState } from "@itwin/core-frontend";

import { ITwinRealityData, RealityDataAccessClient, RealityDataResponse } from "@itwin/reality-data-client";
import { PlanarClipMaskSettings, FeatureAppearance } from "@itwin/core-common";

import { cMessage } from "./Message";
import { cClassifier } from "./Classifier";
import { cSelection } from "./Selection";
import { cView } from "./View";
import { ClipPrimitive, ClipShape, ClipVector, Matrix3d, Point3d, Transform, UnionOfConvexClipPlaneSets, Vector3d } from "@itwin/core-geometry";
import { cMultiViewport } from "./MultiViewport";

// !!! AH
// Class is made on App init and models are fetched from BENTLEY CONTEXTSHARE

export class cRealityData {
  private static iModel: IModelConnection;
  public static sViewPort: ScreenViewport;
  private static rModelName: string;
  private static availableModels: ContextRealityModelProps[] = [];
  public static rModelId: ContextRealityModelState[] = [];

  public static initRealityModel(vp: ScreenViewport, imodel: IModelConnection) {
    console.log("initRealityModel");
    this.iModel = imodel;
    this.sViewPort = vp;
    this.rModelName = "";
    this.loadRealityModels();
  }

  public static async loadRealityModels() {
    this.availableModels = await this.fetchRealityModels();
  }

  private static createRealityDataSourceKeyFromITwinRealityData(iTwinRealityData: ITwinRealityData): RealityDataSourceKey {
    return {
      provider: RealityDataProvider.ContextShare,
      format:
        iTwinRealityData.type === "OPC"
          ? RealityDataFormat.OPC
          : RealityDataFormat.ThreeDTile,
      id: iTwinRealityData.id,
    };
  }

  private static async fetchRealityModels(): Promise<ContextRealityModelProps[]> {
    const accessToken = await IModelApp.authorizationClient!.getAccessToken();
    let available: RealityDataResponse = { realityDatas: [] };
    available = await new RealityDataAccessClient().getRealityDatas(accessToken, this.iModel.iTwinId, undefined);

    const availableModels: ContextRealityModelProps[] = [];

    for (const rdEntry of available.realityDatas) {
      const name = undefined !== rdEntry.displayName ? rdEntry.displayName : rdEntry.id;
      const rdSourceKey = {
        provider: RealityDataProvider.ContextShare,
        format:
          rdEntry.type === "OPC"
            ? RealityDataFormat.OPC
            : RealityDataFormat.ThreeDTile,
        id: rdEntry.id,
      };

      const tilesetUrl = await IModelApp.realityDataAccess?.getRealityDataUrl(this.iModel.iTwinId, rdSourceKey.id);
      if (tilesetUrl) {
        const classifier = [];
        const crmProp: ContextRealityModelProps = {
          classifiers: classifier,
          rdSourceKey,
          tilesetUrl,
          name,
          description: rdEntry?.description,
          realityDataId: rdSourceKey.id,
        };

        availableModels.push(crmProp);
      }
    }

    return availableModels;
  }

  // Attaches RM based on Model Name
  public static async attachRealityModel(realityModel: string) {
    if (realityModel === undefined) return;

    const vp = cMultiViewport.getRealityModelViewport();
    for (const model of this.availableModels) {
      if (model.name === realityModel && model.tilesetUrl) {
        const rId = vp.displayStyle.attachRealityModel(model);
        this.rModelName = realityModel;
        this.rModelId.push(rId);

        return;
      }
    }
  }

  public static attachClassifier(classifier?: string) {
    const classifierModel = classifier ?? "$Elements";
    cClassifier.addClassifiersToRealityData();
    cClassifier.setupClassifier(classifierModel);

    cSelection.setOnElementSelected("index");
  }

  public static getRealityModelIds(): ContextRealityModelState[] {
    return this.rModelId;
  }

  public static getRealityModels(): ContextRealityModelProps[] {
    return this.availableModels;
  }

  public static async removeRealityModel() {
    const models: ContextRealityModelState[] = [];

    const vp = cMultiViewport.getRealityModelViewport();
    const style = vp.displayStyle.clone();

    // Collect reality models on displayStyle and detach
    // this.sViewPort.displayStyle.forEachRealityModel(
    vp.displayStyle.forEachRealityModel(
        (modelState: ContextRealityModelState) => {
        models.push(modelState);
      }
    );

    for (const model of models)
      // this.sViewPort.displayStyle.detachRealityModelByNameAndUrl(model.name, model.url);
      vp.displayStyle.detachRealityModelByNameAndUrl(model.name, model.url);

    this.rModelId = [];
  }

  public static async detachRealityModelByName(realityModel: string) {

    const vp = cMultiViewport.getRealityModelViewport();
    const activeModels = this.getActiveRealityModels();
    for (const model of activeModels.models) {
      if (model.name === realityModel) {
        vp.displayStyle.detachRealityModelByNameAndUrl(model.name, model.url);
      }
      vp.invalidateScene();
    }
  }

  public static async detachRealityModelByIndex(id: number) {
    const Models = this.availableModels;
    if (Models[id]) {
      const style = this.sViewPort.displayStyle.clone();

      const modelName = Models[id].name;
      this.detachRealityModelByName(modelName!);
    //   const activeModels = this.getActiveRealityModels();

    // for (const model of activeModels.models) {
    //   if (model.name === modelName) {
    //     this.sViewPort.displayStyle.detachRealityModelByNameAndUrl(model.name, model.url);
    //   }
    // }
  }
  }

  // Modify reality data background transparency using the Viewport API
  public static async setRealityDataTransparency(transparency: number) {
    // For this example we want to affect the appearance of active reality models
    const activeModels = this.getActiveRealityModels();
    for (const model of activeModels.models) {
      if (model) {
        model.appearanceOverrides = model.appearanceOverrides
          ? model.appearanceOverrides.clone({ transparency })
          : FeatureAppearance.fromJSON({ transparency });
      }
    }
  }

  public static getActiveRealityModels(): ContextRealityModels {
//    const cRM = this.sViewPort.displayStyle.settings.contextRealityModels;
    const vp = cView.getCurrentView();
    const cRM = vp.displayStyle.settings.contextRealityModels;

    return cRM;
  }

  public static getActiveRealityModelName(): string {
    return this.rModelName;
  }

  public static emptyRealityModels() {
    this.availableModels = [];
    this.rModelName = "";
    this.rModelId = [];
  }

public static createPathClip(points: Point3d[], radius: number, nSides: number): ClipVector | undefined {
    if (points.length < 2)
      return undefined;

    // Get polygon shape points (xy only) for given radius and number of sides...
    const xyPts: Point3d[] = [];
    const delta = (Math.PI * 2.0) / nSides;
    let angle = 0;
    const center = Point3d.create();
    const tmp = Point3d.create();
    for (let iPoly = 0; iPoly < nSides; iPoly++, angle += delta) {
      tmp.x = radius * Math.cos(angle);
      tmp.y = radius * Math.sin(angle);
      tmp.z = 0;
      xyPts.push(center.plus(tmp));
    }
    if (xyPts.length < 3)
    return undefined;

    // Create empty UnionOfConvexClipPlaneSets...
    const planeSets = UnionOfConvexClipPlaneSets.createEmpty();

    // Create a ClipShape for each segment of path as easy way to get a ConvexClipPlaneSet...
    for (let iPt = 0; iPt < points.length - 1; ++iPt) {
      const dir = Vector3d.createStartEnd(points[iPt], points[iPt + 1]);
      const matrix = Matrix3d.createRigidHeadsUp(dir);
      const transform = Transform.createOriginAndMatrix(points[iPt], matrix);
      const zLow = transform.multiplyInversePoint3d(points[iPt]);
      const zHigh = transform.multiplyInversePoint3d(points[iPt + 1]);
  
      const segmentClip = ClipShape.createShape(xyPts, zLow?.z, zHigh?.z, transform);
      if (undefined === segmentClip)
        return undefined;
  
      const segmentPlanes = segmentClip.fetchClipPlanesRef();
      if (undefined === segmentPlanes)
        return undefined;
  
      for (const convexSet of segmentPlanes.convexSets)
        planeSets.addConvexSet(convexSet);
    }
    
    const primitive = ClipPrimitive.createCapture(planeSets);
    return ClipVector.createCapture([primitive]);

  }

  public static async attachMask(maskModel: ContextRealityModelState, maskElem: Set<string>) {   
    if (maskElem.size !== 0) {
      //const points = await cSelection.getSelectionPoints();
     // const clipVector = this.createPathClip(points, 1.0, 6);

    //  ViewClipTool.setViewClip(this.sViewPort, clipVector);

      this.removeRealityModel();
      const viewState = this.sViewPort.view;
      
      viewState.displayStyle.settings.viewFlags =
        viewState.displayStyle.viewFlags.with("backgroundMap", true);
      
      const modelIds = await cView.fetchModelIds("$Elements");
    
      // Mask Config Settings
      const mask = PlanarClipMaskSettings.create({
        invert: true,
        modelIds: modelIds,
        elementIds: maskElem,
        transparency: 0.1,
      });
//      maskModel.planarClipMaskSettings = mask;
//      console.log(maskModel);
      for (const model of this.availableModels) {
        if (model.name === maskModel.name) {
          try {
            const rId = this.sViewPort.displayStyle.attachRealityModel(model)!;
            if (rId) {
              try {
                // Setting mask settings to model
                rId.planarClipMaskSettings = mask;
              } catch (err) {
                console.log(err);
              }
            }
            this.rModelName = maskModel.name;
            this.rModelId.push(rId);

          } catch (err) {
            console.log(err);
          }
        }
      }

      if (viewState.isSpatialView())
        viewState.displayStyle.settings.toggleSkyBox(false);

      this.iModel.hilited.elements.addIds(maskElem);
    } else {
      cMessage.showStatusBarError("Element Not Selected");
    }
  }

  public static removeMask(maskModel) {
    maskModel.planarClipMaskSettings = undefined;
    
    const viewState = this.sViewPort.view;
    viewState.displayStyle.settings.viewFlags =
    viewState.displayStyle.viewFlags.with("backgroundMap", false);

    this.attachClassifier("$Elements");
  }

}
